import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { TabPanel, TabView } from 'primereact/tabview';
import { Tag } from 'primereact/tag';
import { toast } from 'react-toastify';
import { endPoints } from '../../Config';
import { handleGetRequest } from '../../service/GetTemplate';
import { handlePatchRequest } from '../../service/PatchTemplate';
import { handlePostRequest } from '../../service/PostTemplate';
import { exportToCsv, exportToExcel } from '../../utilities/exportToExcel';
import './RevenueReconciliation.scss';

const money = (value) =>
    new Intl.NumberFormat('es-PA', { style: 'currency', currency: 'USD' }).format(Number(value || 0));

const percent = (value) => `${(Number(value || 0) * 100).toFixed(1)}%`;

const currentPanamaPeriod = () => {
    const parts = new Intl.DateTimeFormat('en', {
        timeZone: 'America/Panama',
        year: 'numeric',
        month: '2-digit'
    }).formatToParts(new Date());
    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    return `${year}-${month}`;
};

const downloadJson = (value, filename) => {
    const blob = new Blob([`${JSON.stringify(value, null, 2)}\n`], { type: 'application/json;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

const dateTime = (value) => {
    if (!value) return 'Pendiente';
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? 'Pendiente' : parsed.toLocaleString('es-PA');
};

const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('No se pudo leer el archivo seleccionado.'));
    reader.onload = () => {
        const bytes = new Uint8Array(reader.result);
        let binary = '';
        const chunkSize = 32768;
        for (let index = 0; index < bytes.length; index += chunkSize) {
            binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
        }
        resolve(window.btoa(binary));
    };
    reader.readAsArrayBuffer(file);
});

const statusLabels = {
    draft: 'Borrador',
    in_review: 'En revisión',
    closed: 'Cerrado',
    reopened: 'Reabierto',
    ready: 'Lista',
    pending: 'Pendiente',
    error: 'Error',
    approved: 'Aprobado',
    rejected: 'Rechazado',
    idle: 'Actualizada',
    running: 'Generando',
    failed: 'Falló la generación'
};

const severity = (status) => {
    if (['closed', 'ready', 'approved'].includes(status)) return 'success';
    if (['error', 'failed', 'rejected'].includes(status)) return 'danger';
    if (['in_review', 'pending', 'reopened', 'running'].includes(status)) return 'warning';
    return 'info';
};

const Metric = ({ label, value, note }) => (
    <Card className="revenue-reconciliation__metric">
        <span>{label}</span>
        <strong>{value}</strong>
        {note ? <small>{note}</small> : null}
    </Card>
);

const RevenueReconciliation = () => {
    const defaultPeriod = currentPanamaPeriod();
    const dispatch = useDispatch();
    const [periods, setPeriods] = useState([]);
    const [selectedPeriodId, setSelectedPeriodId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [trend, setTrend] = useState([]);
    const [trendRange, setTrendRange] = useState('6');
    const [items, setItems] = useState([]);
    const [itemPagination, setItemPagination] = useState({ page: 1, limit: 50, total: 0 });
    const [itemMappedFilter, setItemMappedFilter] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const [month, setMonth] = useState(defaultPeriod);
    const [busy, setBusy] = useState(false);
    const [adjustmentOpen, setAdjustmentOpen] = useState(false);
    const [adjustment, setAdjustment] = useState({
        category: '',
        amount: null,
        reason: '',
        evidenceReference: '',
        routeName: '',
        machineName: ''
    });
    const [sourceConfig, setSourceConfig] = useState(null);
    const [accountIds, setAccountIds] = useState([]);
    const [mappings, setMappings] = useState([]);
    const [mapping, setMapping] = useState({
        source: 'quickbooks',
        sourceId: '',
        sourceLabel: '',
        canonicalRouteId: '',
        canonicalRouteName: '',
        effectiveFrom: `${defaultPeriod}-01`
    });
    const [banescoFile, setBanescoFile] = useState(null);
    const [banescoFileContent, setBanescoFileContent] = useState('');
    const [banescoMerchantContract, setBanescoMerchantContract] = useState('');
    const [banescoQueryDateMode, setBanescoQueryDateMode] = useState('transaction');
    const [banescoCoverageStart, setBanescoCoverageStart] = useState('');
    const [banescoCoverageEnd, setBanescoCoverageEnd] = useState('');
    const [banescoPreview, setBanescoPreview] = useState(null);
    const [banescoSource, setBanescoSource] = useState({ batches: [], terminals: [], machines: [], automation: {} });
    const [banescoSuggestions, setBanescoSuggestions] = useState(null);
    const [selectedBanescoSuggestions, setSelectedBanescoSuggestions] = useState([]);
    const [banescoMappingDraft, setBanescoMappingDraft] = useState(null);
    const [yappySource, setYappySource] = useState({ batches: [], machines: [], totals: {}, automation: {} });

    const period = detail?.period;
    const totals = period?.totals || {};

    const loadPeriods = useCallback(async (keepSelection = true) => {
        const response = await handleGetRequest(endPoints.revenueReconciliationPeriods, false, false, dispatch);
        const nextPeriods = Array.isArray(response?.data) ? response.data : [];
        setPeriods(nextPeriods);
        if (!keepSelection && nextPeriods.length) setSelectedPeriodId(nextPeriods[0]._id);
        return nextPeriods;
    }, [dispatch]);

    const loadDetail = useCallback(async (periodId) => {
        if (!periodId) {
            setDetail(null);
            return;
        }
        const response = await handleGetRequest(endPoints.revenueReconciliationPeriod(periodId), false, false, dispatch);
        setDetail(response?.data || null);
    }, [dispatch]);

    const loadTrend = useCallback(async () => {
        const response = await handleGetRequest(endPoints.revenueReconciliationTrend(trendRange), false, false, dispatch);
        setTrend(Array.isArray(response?.data) ? [...response.data].reverse() : []);
    }, [dispatch, trendRange]);

    const loadConfig = useCallback(async () => {
        const response = await handleGetRequest(endPoints.revenueReconciliationConfig, false, false, dispatch);
        const config = response?.data || null;
        setSourceConfig(config);
        setAccountIds(config?.quickBooksRevenueAccountIds || []);
    }, [dispatch]);

    const loadMappings = useCallback(async () => {
        const response = await handleGetRequest(endPoints.revenueReconciliationMappings, false, false, dispatch);
        setMappings(Array.isArray(response?.data) ? response.data : []);
    }, [dispatch]);

    const loadBanescoSource = useCallback(async (periodKey) => {
        if (!/^\d{4}-\d{2}$/.test(periodKey || '')) return;
        const response = await handleGetRequest(
            endPoints.revenueReconciliationBanescoSource(periodKey),
            false,
            false,
            dispatch
        );
        setBanescoSource(response?.data || { batches: [], terminals: [], machines: [] });
    }, [dispatch]);

    const loadYappySource = useCallback(async (periodKey) => {
        if (!/^\d{4}-\d{2}$/.test(periodKey || '')) return;
        const response = await handleGetRequest(
            endPoints.revenueReconciliationYappySource(periodKey),
            false,
            false,
            dispatch
        );
        setYappySource(response?.data || { batches: [], machines: [], totals: {}, automation: {} });
    }, [dispatch]);

    useEffect(() => {
        const initialize = async () => {
            const nextPeriods = await loadPeriods(true);
            if (nextPeriods.length) setSelectedPeriodId(nextPeriods[0]._id);
            await Promise.all([loadConfig(), loadMappings()]);
        };
        initialize();
    }, [loadConfig, loadMappings, loadPeriods]);

    useEffect(() => {
        loadTrend();
    }, [loadTrend]);

    useEffect(() => {
        loadDetail(selectedPeriodId);
    }, [loadDetail, selectedPeriodId]);

    useEffect(() => {
        if (!selectedPeriodId || detail?.period?.generationStatus !== 'running') return undefined;
        const timer = window.setInterval(async () => {
            const response = await handleGetRequest(
                endPoints.revenueReconciliationPeriod(selectedPeriodId),
                false,
                false,
                dispatch
            );
            const nextDetail = response?.data || null;
            setDetail(nextDetail);
            await loadPeriods(true);
            if (nextDetail?.period?.generationStatus !== 'running') await loadTrend();
        }, 3000);
        return () => window.clearInterval(timer);
    }, [detail?.period?.generationStatus, dispatch, loadPeriods, loadTrend, selectedPeriodId]);

    const loadItems = useCallback(async (level, page = 1, limit = 50) => {
        if (!selectedPeriodId) return;
        const query = { level, page: String(page), limit: String(limit) };
        if (itemMappedFilter !== '') query.mapped = itemMappedFilter;
        const params = new URLSearchParams(query);
        const response = await handleGetRequest(
            endPoints.revenueReconciliationItems(selectedPeriodId, params.toString()),
            false,
            false,
            dispatch
        );
        setItems(response?.data?.items || []);
        setItemPagination(response?.data?.pagination || { page, limit, total: 0 });
    }, [dispatch, itemMappedFilter, selectedPeriodId]);

    useEffect(() => {
        if (activeIndex === 1) loadItems('route', 1, itemPagination.limit);
        if (activeIndex === 2) loadItems('machine', 1, itemPagination.limit);
        if (activeIndex === 3) {
            loadBanescoSource(month);
            loadYappySource(month);
        }
    }, [activeIndex, itemPagination.limit, loadBanescoSource, loadItems, loadYappySource, month]);

    useEffect(() => {
        if (activeIndex !== 3 || !banescoSource.automation?.running) return undefined;
        const timer = window.setInterval(() => loadBanescoSource(month), 5000);
        return () => window.clearInterval(timer);
    }, [activeIndex, banescoSource.automation?.running, loadBanescoSource, month]);

    useEffect(() => {
        if (activeIndex !== 3 || !yappySource.automation?.running) return undefined;
        const timer = window.setInterval(() => loadYappySource(month), 5000);
        return () => window.clearInterval(timer);
    }, [activeIndex, loadYappySource, month, yappySource.automation?.running]);

    const refreshAll = async (periodId = selectedPeriodId) => {
        await Promise.all([loadPeriods(true), loadTrend()]);
        if (periodId) await loadDetail(periodId);
    };

    const generate = async () => {
        if (!/^\d{4}-\d{2}$/.test(month)) {
            toast.warn('Seleccione un mes válido.');
            return;
        }
        setBusy(true);
        const current = periods.find((item) => item.periodKey === month && item.isCurrent);
        const response = await dispatch(
            handlePostRequest(
                { expectedRevision: current?.revision ?? null },
                endPoints.revenueReconciliationGenerate(month),
                false,
                true
            )
        );
        const generated = response?.data?.data;
        if (generated?.period?._id) {
            setSelectedPeriodId(generated.period._id);
            setDetail(generated);
            await refreshAll(generated.period._id);
        }
        setBusy(false);
    };

    const periodAction = async (action, payload = {}) => {
        if (!period) return;
        setBusy(true);
        const endpoint = {
            submit: endPoints.revenueReconciliationSubmit,
            close: endPoints.revenueReconciliationClose,
            reopen: endPoints.revenueReconciliationReopen
        }[action](period._id);
        const response = await dispatch(
            handlePostRequest({ ...payload, expectedRevision: period.revision }, endpoint, false, true)
        );
        const result = response?.data?.data;
        if (result?.period?._id) {
            setSelectedPeriodId(result.period._id);
            setDetail(result);
            await refreshAll(result.period._id);
        }
        setBusy(false);
    };

    const saveAdjustment = async () => {
        if (!period) return;
        const response = await dispatch(
            handlePostRequest(
                { ...adjustment, expectedPeriodRevision: period.revision },
                endPoints.revenueReconciliationAdjustments(period._id),
                false,
                true
            )
        );
        if (response?.status >= 200 && response?.status < 300) {
            setAdjustmentOpen(false);
            setAdjustment({ category: '', amount: null, reason: '', evidenceReference: '', routeName: '', machineName: '' });
            await refreshAll(period._id);
        }
    };

    const decideAdjustment = async (item, decision) => {
        const reason = decision === 'rejected' ? window.prompt('Motivo del rechazo:') : '';
        if (decision === 'rejected' && !reason) return;
        const response = await dispatch(
            handlePatchRequest(
                { decision, reason, expectedRevision: item.revision },
                endPoints.revenueReconciliationAdjustmentDecision(item._id),
                false,
                true
            )
        );
        if (response?.status >= 200 && response?.status < 300) await refreshAll(period._id);
    };

    const saveSourceConfig = async () => {
        if (!period) {
            toast.warn('Seleccione un período para registrar el cambio en la auditoría.');
            return;
        }
        const changeReason = window.prompt('Motivo obligatorio del cambio de cuentas de ingresos:');
        if (!changeReason?.trim()) return;
        const selectedAccounts = (sourceConfig?.availableQuickBooksAccounts || []).filter((account) => accountIds.includes(account.id));
        const response = await dispatch(
            handlePatchRequest(
                {
                    quickBooksRevenueAccountIds: accountIds,
                    quickBooksRevenueAccountLabels: selectedAccounts.map((account) => [account.number, account.name].filter(Boolean).join(' · ')),
                    periodId: period._id,
                    changeReason: changeReason.trim()
                },
                endPoints.revenueReconciliationConfig,
                false,
                true
            )
        );
        if (response?.data?.data) {
            setSourceConfig(response.data.data);
            await loadConfig();
        }
    };

    const refreshQuickBooksAccounts = async () => {
        setBusy(true);
        const response = await dispatch(
            handlePostRequest({}, endPoints.revenueReconciliationRefreshQuickBooksAccounts, false, true)
        );
        if (response?.data?.data) {
            setSourceConfig(response.data.data);
            setAccountIds(response.data.data.quickBooksRevenueAccountIds || []);
        }
        setBusy(false);
    };

    const saveMapping = async () => {
        const response = await dispatch(
            handlePostRequest(mapping, endPoints.revenueReconciliationMappings, false, true)
        );
        if (response?.status >= 200 && response?.status < 300) {
            setMapping({ ...mapping, sourceId: '', sourceLabel: '', canonicalRouteId: '', canonicalRouteName: '' });
            await loadMappings();
        }
    };

    const previewBanescoFile = async () => {
        if (!banescoFile || !banescoMerchantContract.trim()) {
            toast.warn('Seleccione el archivo e indique el contrato de comercio de Banesco.');
            return;
        }
        if (banescoFile.size > 15 * 1024 * 1024) {
            toast.warn('El archivo no puede exceder 15 MB.');
            return;
        }
        setBusy(true);
        try {
            const contentBase64 = await fileToBase64(banescoFile);
            const response = await dispatch(handlePostRequest({
                fileName: banescoFile.name,
                merchantContract: banescoMerchantContract.trim(),
                queryDateMode: banescoQueryDateMode,
                contentBase64
            }, endPoints.revenueReconciliationBanescoPreview, false, true));
            const preview = response?.data?.data;
            if (preview?.summary) {
                setBanescoFileContent(contentBase64);
                setBanescoPreview(preview);
                const postingMode = preview.queryDateMode === 'posting';
                setBanescoCoverageStart(
                    (postingMode ? preview.summary.paymentDateMin : preview.summary.transactionDateMin) || ''
                );
                setBanescoCoverageEnd(
                    (postingMode ? preview.summary.paymentDateMax : preview.summary.transactionDateMax) || ''
                );
            }
        } catch (error) {
            toast.warn(error.message || 'No se pudo analizar el archivo.');
        } finally {
            setBusy(false);
        }
    };

    const importBanescoFile = async () => {
        if (!banescoPreview || !banescoFileContent || !banescoCoverageStart || !banescoCoverageEnd) {
            toast.warn('Analice el archivo y confirme su cobertura antes de importarlo.');
            return;
        }
        setBusy(true);
        const response = await dispatch(handlePostRequest({
            fileName: banescoFile?.name || 'Transactions.csv',
            merchantContract: banescoMerchantContract.trim(),
            coverageStart: banescoCoverageStart,
            coverageEnd: banescoCoverageEnd,
            queryDateMode: banescoQueryDateMode,
            contentBase64: banescoFileContent
        }, endPoints.revenueReconciliationBanescoImport, false, true));
        if (response?.status >= 200 && response?.status < 300) {
            const importedPeriod = banescoCoverageStart.slice(0, 7);
            setMonth(importedPeriod);
            await loadBanescoSource(importedPeriod);
            setBanescoPreview(null);
            setBanescoFileContent('');
            toast.info('Use “Generar / actualizar” para incorporar el lote a la conciliación del período.');
        }
        setBusy(false);
    };

    const pullBanescoPeriod = async () => {
        if (!month) {
            toast.warn('Seleccione el mes que desea sincronizar.');
            return;
        }
        if (!banescoSource.automation?.configured || !banescoSource.automation?.browserAvailable) {
            toast.warn('La conexión automática de Banesco todavía no está configurada en el servidor.');
            return;
        }
        setBusy(true);
        const response = await dispatch(handlePostRequest(
            { periodKey: month },
            endPoints.revenueReconciliationBanescoPull,
            false,
            true
        ));
        if (response?.status >= 200 && response?.status < 300) {
            await loadBanescoSource(month);
            toast.info('La sincronización continuará en segundo plano. Puede permanecer aquí o volver más tarde.');
        }
        setBusy(false);
    };

    const pullYappyPeriod = async () => {
        if (!month) {
            toast.warn('Seleccione el mes que desea sincronizar.');
            return;
        }
        if (!yappySource.automation?.configured) {
            toast.warn('La conexión oficial de ePay / Yappy todavía no está configurada en el servidor.');
            return;
        }
        setBusy(true);
        const response = await dispatch(handlePostRequest(
            { periodKey: month },
            endPoints.revenueReconciliationYappyPull,
            false,
            true
        ));
        if (response?.status >= 200 && response?.status < 300) {
            await loadYappySource(month);
            toast.info('La sincronización de ePay / Yappy continuará en segundo plano.');
        }
        setBusy(false);
    };

    const syncBanescoCatalog = async () => {
        setBusy(true);
        const response = await dispatch(handlePostRequest(
            {},
            endPoints.revenueReconciliationBanescoCatalogPull,
            false,
            true
        ));
        if (response?.status >= 200 && response?.status < 300) {
            await loadBanescoSource(month);
        }
        setBusy(false);
    };

    const calculateBanescoSuggestions = async () => {
        setBusy(true);
        const response = await handleGetRequest(
            endPoints.revenueReconciliationBanescoMappingSuggestions(month),
            false,
            false,
            dispatch
        );
        if (response?.data) {
            setBanescoSuggestions({
                ...response.data,
                suggestions: (response.data.suggestions || []).map((row) => ({
                    ...row,
                    selectionKey: `${row.merchantContract}|${row.terminalId}`
                }))
            });
            setSelectedBanescoSuggestions([]);
            toast.info('Las sugerencias se calcularon sin aplicar ningún cambio. Revise y seleccione las que desea guardar.');
        }
        setBusy(false);
    };

    const applySelectedBanescoSuggestions = async () => {
        if (!selectedBanescoSuggestions.length) {
            toast.warn('Seleccione al menos una sugerencia para guardarla.');
            return;
        }
        setBusy(true);
        const response = await dispatch(handlePostRequest({
            periodKey: month,
            generatedAt: banescoSuggestions?.generatedAt,
            mappings: selectedBanescoSuggestions
        }, endPoints.revenueReconciliationBanescoMappingsBulk, false, true));
        if (response?.status >= 200 && response?.status < 300) {
            setSelectedBanescoSuggestions([]);
            await Promise.all([loadBanescoSource(month), calculateBanescoSuggestions()]);
            toast.info('Use “Generar / actualizar” para aplicar los nuevos mapeos al período.');
        }
        setBusy(false);
    };

    const beginBanescoMapping = (row, parlevelSystemId) => {
        setBanescoMappingDraft({
            row,
            parlevelSystemId,
            verificationStatus: row.mapping?.verificationStatus || 'provisional',
            verificationNote: row.mapping?.verificationNote || ''
        });
    };

    const saveBanescoMapping = async () => {
        if (!banescoMappingDraft?.verificationNote?.trim()) {
            toast.warn('Registre la evidencia o la razón de provisionalidad antes de guardar.');
            return;
        }
        const { row, parlevelSystemId, verificationStatus, verificationNote } = banescoMappingDraft;
        const response = await dispatch(handlePostRequest({
            merchantContract: row.merchantContract,
            terminalId: row.terminalId,
            parlevelSystemId,
            effectiveFrom: `${month}-01`,
            verificationStatus,
            verificationNote: verificationNote.trim()
        }, endPoints.revenueReconciliationBanescoMapping, false, true));
        if (response?.status >= 200 && response?.status < 300) {
            setBanescoMappingDraft(null);
            await loadBanescoSource(month);
            toast.info('Use “Generar / actualizar” para aplicar el nuevo mapeo al período.');
        }
    };

    const exportData = async (format) => {
        if (!period) return;
        const response = await handleGetRequest(endPoints.revenueReconciliationExport(period._id), false, false, dispatch);
        const rows = (response?.data?.items || []).map((item) => ({
            Fuente: item.source,
            Nivel: item.level,
            Ruta: item.routeName || '',
            Máquina: item.machineName || '',
            Ubicación: item.locationName || '',
            Categoría: item.category,
            Importes: JSON.stringify(item.amounts || {}),
            Fórmula: item.formula || '',
            Identificador: item.sourceId,
            Trazabilidad: JSON.stringify(item.trace || {}),
            Mapeado: item.mapped ? 'Sí' : 'No'
        }));
        const filename = `conciliacion-${period.periodKey}-v${period.version}`;
        if (format === 'csv') exportToCsv(rows, filename);
        else exportToExcel(rows, filename);
    };

    const exportAuditJson = async () => {
        if (!period) return;
        const response = await handleGetRequest(endPoints.revenueReconciliationExport(period._id), false, false, dispatch);
        if (response?.data) downloadJson(response.data, `conciliacion-auditada-${period.periodKey}-v${period.version}`);
    };

    const periodOptions = periods.map((item) => ({
        label: `${item.periodKey} · v${item.version} · ${statusLabels[item.status] || item.status}${item.generationStatus === 'running' ? ' · Generando' : ''}`,
        value: item._id
    }));

    const sourceRows = detail?.snapshots || [];
    const generationActive = period?.generationStatus === 'running';
    const routeRows = useMemo(() => items.filter((item) => item.level === 'route'), [items]);
    const machineRows = useMemo(() => items.filter((item) => item.level === 'machine'), [items]);
    const banescoMachineOptions = useMemo(() => (banescoSource.machines || []).map((machine) => ({
        value: machine.systemId,
        label: [machine.systemId, machine.routeName, machine.machineName, machine.locationName].filter(Boolean).join(' · ')
    })), [banescoSource.machines]);

    const amountsBody = (row) => (
        <div className="revenue-reconciliation__amounts">
            {Object.entries(row.amounts || {}).map(([key, value]) => (
                <span key={key}><b>{key}:</b> {money(value)}</span>
            ))}
        </div>
    );

    const adjustmentActions = (row) => row.status === 'pending' ? (
        <div className="revenue-reconciliation__row-actions">
            <Button label="Aprobar" icon="pi pi-check" className="p-button-sm p-button-success" onClick={() => decideAdjustment(row, 'approved')} />
            <Button label="Rechazar" icon="pi pi-times" className="p-button-sm p-button-danger p-button-outlined" onClick={() => decideAdjustment(row, 'rejected')} />
        </div>
    ) : <span>{row.approvedBy?.email || row.rejectedBy?.email || '—'}</span>;

    const banescoMappingBody = (row) => (
        <Dropdown
            value={row.mapping?.parlevelSystemId || null}
            options={banescoMachineOptions}
            onChange={(event) => beginBanescoMapping(row, event.value)}
            optionLabel="label"
            optionValue="value"
            filter
            showClear={false}
            placeholder="Vincular máquina"
            className="revenue-reconciliation__machine-map"
        />
    );

    const banescoMappingStatusBody = (row) => {
        if (!row.mapping) return <Tag value="Sin mapear" severity="warning" />;
        if (row.mapping.verificationStatus === 'provisional') {
            return (
                <Tag
                    value="* Provisional"
                    severity="warning"
                    title={row.mapping.verificationNote || 'Equivalencia pendiente de validación definitiva.'}
                />
            );
        }
        return <Tag value="Verificada" severity="success" title={row.mapping.verificationNote || 'Evidencia registrada'} />;
    };

    return (
        <div className="revenue-reconciliation">
            <div className="revenue-reconciliation__header">
                <div>
                    <span className="revenue-reconciliation__eyebrow">Contabilidad</span>
                    <h1>Conciliación de recaudación</h1>
                    <p>Datos trazables de Parlevel, QuickBooks, Syncro y procesadores oficiales.</p>
                </div>
                <div className="revenue-reconciliation__generate">
                    <input
                        type="month"
                        value={month}
                        onChange={(event) => {
                            setMonth(event.target.value);
                            setBanescoSuggestions(null);
                            setSelectedBanescoSuggestions([]);
                        }}
                        aria-label="Período a generar"
                    />
                    <Button
                        label={generationActive ? 'Generando fuentes…' : 'Generar / actualizar'}
                        icon="pi pi-refresh"
                        loading={busy || generationActive}
                        disabled={generationActive}
                        onClick={generate}
                    />
                </div>
            </div>

            <div className="revenue-reconciliation__toolbar">
                <Dropdown value={selectedPeriodId} options={periodOptions} onChange={(event) => setSelectedPeriodId(event.value)} placeholder="Seleccione un período" />
                {period ? <Tag value={statusLabels[period.status] || period.status} severity={severity(period.status)} /> : null}
                {period?.generationStatus ? <Tag value={statusLabels[period.generationStatus] || period.generationStatus} severity={severity(period.generationStatus)} /> : null}
                <div className="revenue-reconciliation__toolbar-spacer" />
                <Button label="CSV partidas" icon="pi pi-download" className="p-button-outlined" disabled={!period} onClick={() => exportData('csv')} />
                <Button label="XLSX partidas" icon="pi pi-file-excel" className="p-button-success" disabled={!period} onClick={() => exportData('xlsx')} />
                <Button label="JSON auditado" icon="pi pi-shield" className="p-button-outlined" disabled={!period} onClick={exportAuditJson} />
            </div>

            {!period ? (
                <div className="revenue-reconciliation__empty">Genere julio de 2026 o seleccione un período existente.</div>
            ) : (
                <>
                    {generationActive ? (
                        <div className="revenue-reconciliation__blocking revenue-reconciliation__blocking--progress">
                            <i className="pi pi-spin pi-spinner" />
                            <div><strong>Consultando fuentes oficiales</strong><span>Puede permanecer en esta página; los resultados aparecerán automáticamente.</span></div>
                        </div>
                    ) : null}
                    {period.generationStatus === 'failed' ? (
                        <div className="revenue-reconciliation__blocking">
                            <i className="pi pi-exclamation-triangle" />
                            <div><strong>No se pudo completar la generación</strong><span>{period.generationError || 'Intente nuevamente.'}</span></div>
                        </div>
                    ) : null}
                    {period.blockingReasons?.length ? (
                        <div className="revenue-reconciliation__blocking">
                            <i className="pi pi-lock" />
                            <div><strong>El cierre final está bloqueado</strong>{period.blockingReasons.map((reason) => <span key={reason}>{reason}</span>)}</div>
                        </div>
                    ) : null}

                    <TabView activeIndex={activeIndex} onTabChange={(event) => setActiveIndex(event.index)}>
                        <TabPanel header="Resumen">
                            <div className="revenue-reconciliation__metrics">
                                <Metric label="Ventas Parlevel" value={money(totals.parlevel?.totalSales)} note="Rango mensual oficial" />
                                <Metric label="Efectivo esperado" value={money(totals.parlevel?.expectedCash)} />
                                <Metric label="Efectivo recaudado" value={money(totals.parlevel?.collectedCash)} />
                                <Metric label="Diferencia de efectivo" value={money(totals.parlevel?.cashDifference)} note="Recaudado − esperado" />
                                <Metric label="Ingreso neto QuickBooks" value={money(totals.quickbooks?.netRevenue)} />
                                <Metric label="Reembolsos Syncro" value={money(totals.refunds?.refunds)} />
                                <Metric
                                    label="Banesco por fecha de transacción"
                                    value={money(totals.processors?.banesco?.transaction?.gross)}
                                    note="Bruto · By Transaction Date"
                                />
                                <Metric
                                    label="Banesco contabilizado bruto"
                                    value={money(totals.processors?.banesco?.settlement?.gross)}
                                    note="By Posting Date"
                                />
                                <Metric label="Comisiones contabilizadas" value={money(totals.processors?.banesco?.settlement?.fee)} />
                                <Metric label="Neto contabilizado" value={money(totals.processors?.banesco?.settlement?.net)} />
                                <Metric
                                    label="Yappy usado en máquinas"
                                    value={money(totals.processors?.yappy?.used?.amount)}
                                    note="Estado USADO"
                                />
                                <Metric
                                    label="Yappy ingresado"
                                    value={money(totals.processors?.yappy?.entered?.amount)}
                                    note="Estado INGRESADO · no se suma con USADO"
                                />
                                <Metric
                                    label="Control de efectivo"
                                    value={money(totals.controls?.cash?.difference)}
                                    note={`Tolerancia: ${money(totals.controls?.cash?.tolerance)} · ${totals.controls?.cash?.withinTolerance ? 'Dentro de tolerancia' : 'Bloquea cierre'}`}
                                />
                                <Metric
                                    label="Control procesadores vs. Parlevel"
                                    value={money(totals.controls?.processors?.difference)}
                                    note={`Tolerancia: ${money(totals.controls?.processors?.tolerance)} · ${totals.controls?.processors?.withinTolerance ? 'Dentro de tolerancia' : 'Bloquea cierre'}`}
                                />
                            </div>
                            <div className="revenue-reconciliation__split">
                                <Card title="Separación de medios">
                                    <p>Efectivo: <b>{money(totals.parlevel?.cashSales)}</b></p>
                                    <p>Cashless: <b>{money(totals.parlevel?.cashlessSales)}</b></p>
                                    <p>Wallet dentro de cashless: <b>{money(totals.parlevel?.walletSales)}</b></p>
                                    <small>Wallet se muestra como desglose y no vuelve a sumarse.</small>
                                </Card>
                                <Card title="Tendencia de períodos">
                                    <Dropdown
                                        value={trendRange}
                                        options={[
                                            { label: '6 meses', value: '6' },
                                            { label: '12 meses', value: '12' },
                                            { label: 'Año a la fecha', value: 'ytd' }
                                        ]}
                                        onChange={(event) => setTrendRange(event.value)}
                                        className="revenue-reconciliation__trend-range"
                                    />
                                    {trend.length ? trend.map((row) => (
                                        <div className="revenue-reconciliation__trend" key={`${row.periodKey}-${row.version}`}>
                                            <span>{row.periodKey} · v{row.version}</span>
                                            <b>{money(row.totals?.parlevel?.totalSales)}</b>
                                            <Tag value={statusLabels[row.status] || row.status} severity={severity(row.status)} />
                                        </div>
                                    )) : <p>No hay meses generados todavía.</p>}
                                </Card>
                            </div>
                        </TabPanel>
                        <TabPanel header={`Rutas (${activeIndex === 1 ? itemPagination.total : '…'})`}>
                            <div className="revenue-reconciliation__row-actions">
                                <label>Mapeo</label>
                                <Dropdown
                                    value={itemMappedFilter}
                                    options={[
                                        { label: 'Todas', value: '' },
                                        { label: 'Solo sin mapear', value: 'false' },
                                        { label: 'Solo mapeadas', value: 'true' }
                                    ]}
                                    onChange={(event) => setItemMappedFilter(event.value)}
                                />
                                <small>{itemPagination.total} partida(s) encontradas.</small>
                            </div>
                            <DataTable
                                value={routeRows}
                                lazy
                                paginator
                                first={(itemPagination.page - 1) * itemPagination.limit}
                                rows={itemPagination.limit}
                                totalRecords={itemPagination.total}
                                rowsPerPageOptions={[25, 50, 100, 250]}
                                onPage={(event) => loadItems('route', event.page + 1, event.rows)}
                                emptyMessage="No hay partidas por ruta."
                            >
                                <Column field="source" header="Fuente" />
                                <Column field="sourceId" header="ID oficial" />
                                <Column field="routeName" header="Ruta" />
                                <Column field="machineName" header="Máquina / POS" />
                                <Column field="locationName" header="Ubicación" />
                                <Column field="category" header="Categoría" />
                                <Column header="Importes" body={amountsBody} />
                                <Column header="Mapeo" body={(row) => <Tag value={row.mapped ? 'Mapeada' : 'Sin mapear'} severity={row.mapped ? 'success' : 'warning'} />} />
                            </DataTable>
                        </TabPanel>
                        <TabPanel header={`Máquinas (${activeIndex === 2 ? itemPagination.total : '…'})`}>
                            <div className="revenue-reconciliation__row-actions">
                                <label>Mapeo</label>
                                <Dropdown
                                    value={itemMappedFilter}
                                    options={[
                                        { label: 'Todas', value: '' },
                                        { label: 'Solo sin mapear', value: 'false' },
                                        { label: 'Solo mapeadas', value: 'true' }
                                    ]}
                                    onChange={(event) => setItemMappedFilter(event.value)}
                                />
                                <small>{itemPagination.total} partida(s) encontradas.</small>
                            </div>
                            <DataTable
                                value={machineRows}
                                lazy
                                paginator
                                first={(itemPagination.page - 1) * itemPagination.limit}
                                rows={itemPagination.limit}
                                totalRecords={itemPagination.total}
                                rowsPerPageOptions={[25, 50, 100, 250]}
                                onPage={(event) => loadItems('machine', event.page + 1, event.rows)}
                                emptyMessage="No hay partidas por máquina."
                            >
                                <Column field="sourceId" header="ID oficial" />
                                <Column field="routeName" header="Ruta" />
                                <Column field="machineName" header="Máquina" />
                                <Column field="locationName" header="Ubicación" />
                                <Column header="Importes" body={amountsBody} />
                                <Column field="formula" header="Fórmula" />
                            </DataTable>
                        </TabPanel>
                        <TabPanel header="Fuentes">
                            <DataTable value={sourceRows} emptyMessage="Genere el período para consultar las fuentes.">
                                <Column field="source" header="Fuente" />
                                <Column header="Estado" body={(row) => <Tag value={statusLabels[row.status] || row.status} severity={severity(row.status)} />} />
                                <Column field="recordCount" header="Registros" />
                                <Column header="Sincronizada" body={(row) => dateTime(row.syncedAt)} />
                                <Column field="checksum" header="Checksum" body={(row) => <code>{String(row.checksum || '').slice(0, 12)}</code>} />
                                <Column field="message" header="Observación" />
                            </DataTable>
                            <Card title="Transacciones y contabilizaciones oficiales de Banesco" className="revenue-reconciliation__source-config">
                                <p>La sincronización consulta por separado “By Transaction Date” y “By Posting Date”. Las credenciales permanecen fuera de la base de datos y nunca se guarda la tarjeta enmascarada.</p>
                                <div className="revenue-reconciliation__row-actions">
                                    <Button
                                        label={`Sincronizar transacciones + contabilizaciones ${month}`}
                                        icon="pi pi-refresh"
                                        loading={busy || banescoSource.automation?.running}
                                        disabled={!banescoSource.automation?.configured || !banescoSource.automation?.browserAvailable}
                                        onClick={pullBanescoPeriod}
                                    />
                                    <Button
                                        label="Actualizar catálogo oficial de POS"
                                        icon="pi pi-list"
                                        className="p-button-outlined"
                                        loading={busy}
                                        disabled={!banescoSource.automation?.configured || !banescoSource.automation?.browserAvailable}
                                        onClick={syncBanescoCatalog}
                                    />
                                    <Tag
                                        value={banescoSource.automation?.configured && banescoSource.automation?.browserAvailable ? 'Conexión configurada' : 'Configuración pendiente'}
                                        severity={banescoSource.automation?.configured && banescoSource.automation?.browserAvailable ? 'success' : 'warning'}
                                    />
                                    {banescoSource.automation?.running ? (
                                        <Tag value="Sincronizando ambos criterios de fecha" severity="info" />
                                    ) : null}
                                    {banescoSource.automation?.catalogTerminals ? (
                                        <Tag
                                            value={`${banescoSource.automation.catalogTerminals} terminales en catálogo oficial`}
                                            severity="info"
                                        />
                                    ) : null}
                                    {!banescoSource.automation?.running && banescoSource.automation?.lastRun?.status === 'completed' ? (
                                        <Tag
                                            value={`Completada · Transacción: ${banescoSource.automation.lastRun.transactionRecords || 0} · Contabilización: ${banescoSource.automation.lastRun.postingRecords || 0}`}
                                            severity="success"
                                        />
                                    ) : null}
                                    {!banescoSource.automation?.running && banescoSource.automation?.lastRun?.status === 'failed' ? (
                                        <Tag value={`Última sincronización falló: ${banescoSource.automation.lastRun.message}`} severity="danger" />
                                    ) : null}
                                </div>
                                <div className="revenue-reconciliation__banesco-preview">
                                    <div><span>Transacciones del mes</span><b>{banescoSource.dateModes?.transaction?.records || 0}</b></div>
                                    <div><span>Bruto por transacción</span><b>{money(banescoSource.dateModes?.transaction?.gross)}</b></div>
                                    <div><span>Contabilizaciones del mes</span><b>{banescoSource.dateModes?.posting?.records || 0}</b></div>
                                    <div><span>Bruto contabilizado</span><b>{money(banescoSource.dateModes?.posting?.gross)}</b></div>
                                    <div><span>Comisiones contabilizadas</span><b>{money(banescoSource.dateModes?.posting?.fee)}</b></div>
                                    <div><span>Neto contabilizado</span><b>{money(banescoSource.dateModes?.posting?.net)}</b></div>
                                    <div><span>Sin Fecha de Pago informada</span><b>{money(banescoSource.dateModes?.posting?.missingPaymentAmount)}</b></div>
                                </div>
                                <h4>Importación manual de respaldo</h4>
                                <div className="revenue-reconciliation__banesco-import">
                                    <label>
                                        Criterio de fecha del archivo *
                                        <Dropdown
                                            value={banescoQueryDateMode}
                                            options={[
                                                { label: 'Fecha de transacción', value: 'transaction' },
                                                { label: 'Fecha de contabilización (Posting Date)', value: 'posting' }
                                            ]}
                                            onChange={(event) => {
                                                setBanescoQueryDateMode(event.value);
                                                setBanescoPreview(null);
                                                setBanescoFileContent('');
                                            }}
                                        />
                                    </label>
                                    <label>
                                        Contrato de comercio *
                                        <InputText
                                            value={banescoMerchantContract}
                                            onChange={(event) => {
                                                setBanescoMerchantContract(event.target.value);
                                                setBanescoPreview(null);
                                                setBanescoFileContent('');
                                            }}
                                            placeholder="Contrato mostrado en el portal"
                                        />
                                    </label>
                                    <label>
                                        Archivo oficial *
                                        <input
                                            type="file"
                                            accept=".csv,text/csv,text/tab-separated-values"
                                            onChange={(event) => {
                                                setBanescoFile(event.target.files?.[0] || null);
                                                setBanescoPreview(null);
                                                setBanescoFileContent('');
                                            }}
                                        />
                                    </label>
                                    <Button label="Analizar archivo" icon="pi pi-search" className="p-button-outlined" loading={busy} onClick={previewBanescoFile} />
                                </div>
                                {banescoPreview?.summary ? (
                                    <div className="revenue-reconciliation__banesco-preview">
                                        <div><span>Registros</span><b>{banescoPreview.summary.records}</b></div>
                                        <div><span>Procesadas</span><b>{banescoPreview.summary.processedTransactions}</b></div>
                                        <div><span>Volumen procesado</span><b>{money(banescoPreview.summary.processedAmount)}</b></div>
                                        <div><span>Reversos</span><b>{banescoPreview.summary.reversalEvents}</b></div>
                                        <div><span>Bruto</span><b>{money(banescoPreview.summary.gross)}</b></div>
                                        <div><span>Comisiones</span><b>{money(banescoPreview.summary.fee)}</b></div>
                                        <div><span>Neto</span><b>{money(banescoPreview.summary.net)}</b></div>
                                        <div><span>Terminales</span><b>{banescoPreview.summary.terminals}</b></div>
                                        <label>
                                            Cobertura desde *
                                            <input type="date" value={banescoCoverageStart} onChange={(event) => setBanescoCoverageStart(event.target.value)} />
                                        </label>
                                        <label>
                                            Cobertura hasta *
                                            <input type="date" value={banescoCoverageEnd} onChange={(event) => setBanescoCoverageEnd(event.target.value)} />
                                        </label>
                                        {(banescoPreview.summary.warnings || []).map((warning) => <small key={warning}>{warning}</small>)}
                                        <Button label="Importar lote oficial" icon="pi pi-upload" loading={busy} disabled={!banescoPreview.readyToImport} onClick={importBanescoFile} />
                                    </div>
                                ) : null}
                                <h4>Lotes importados para {month}</h4>
                                <DataTable value={banescoSource.batches || []} paginator rows={5} emptyMessage="No hay lotes de Banesco para este período.">
                                    <Column header="Archivo" body={(row) => row.metadata?.fileName || '—'} />
                                    <Column
                                        header="Criterio"
                                        body={(row) => row.metadata?.queryDateMode === 'posting' ? 'Contabilización' : 'Transacción'}
                                    />
                                    <Column header="Cobertura" body={(row) => `${row.metadata?.coverageStart || '—'} – ${row.metadata?.coverageEnd || '—'}`} />
                                    <Column field="recordCount" header="Registros" />
                                    <Column header="Estado" body={(row) => <Tag value={row.status} severity={row.status === 'validated' ? 'success' : 'danger'} />} />
                                    <Column header="Recibido" body={(row) => dateTime(row.receivedAt)} />
                                    <Column header="Checksum" body={(row) => <code>{String(row.checksum || '').slice(0, 12)}</code>} />
                                </DataTable>
                            </Card>
                            <Card title={`Terminales Banesco y máquinas (${(banescoSource.terminals || []).length})`} className="revenue-reconciliation__source-config">
                                <p>Cada terminal debe vincularse con su máquina oficial de Parlevel antes del cierre.</p>
                                <p><b>* Provisional:</b> equivalencia aceptada para conciliar, pero pendiente de validación definitiva.</p>
                                <DataTable value={banescoSource.terminals || []} paginator rows={20} emptyMessage="Importe un lote para identificar terminales.">
                                    <Column field="merchantContract" header="Contrato" />
                                    <Column field="terminalId" header="Terminal" />
                                    <Column header="Nombre oficial" body={(row) => row.catalog?.displayName || 'No encontrada en catálogo'} />
                                    <Column field="records" header="Registros" />
                                    <Column header="Bruto" body={(row) => money(row.gross)} />
                                    <Column header="Neto" body={(row) => money(row.net)} />
                                    <Column header="Máquina Parlevel" body={banescoMappingBody} />
                                    <Column header="Estado" body={banescoMappingStatusBody} />
                                </DataTable>
                                <div className="revenue-reconciliation__suggestion-actions">
                                    <div>
                                        <h4>Sugerencias de vinculación</h4>
                                        <p>Se comparan los importes diarios oficiales de Banesco con las ventas cashless diarias de Parlevel. Toda sugerencia guardada queda provisional hasta contar con evidencia física u oficial.</p>
                                    </div>
                                    <Button
                                        label="Calcular sugerencias"
                                        icon="pi pi-chart-line"
                                        className="p-button-outlined"
                                        loading={busy}
                                        onClick={calculateBanescoSuggestions}
                                    />
                                    <Button
                                        label={`Guardar seleccionadas (${selectedBanescoSuggestions.length})`}
                                        icon="pi pi-check"
                                        disabled={!selectedBanescoSuggestions.length}
                                        loading={busy}
                                        onClick={applySelectedBanescoSuggestions}
                                    />
                                </div>
                                {banescoSuggestions ? (
                                    <>
                                        <div className="revenue-reconciliation__row-actions">
                                            <Tag value={`Alta: ${banescoSuggestions.counts?.high || 0}`} severity="success" />
                                            <Tag value={`Media: ${banescoSuggestions.counts?.medium || 0}`} severity="warning" />
                                            <Tag value={`Baja: ${banescoSuggestions.counts?.low || 0}`} severity="danger" />
                                            <small>Las filas comienzan sin seleccionar. Alta y media se guardan como provisionales; baja requiere vinculación manual.</small>
                                        </div>
                                        <DataTable
                                            value={banescoSuggestions.suggestions || []}
                                            selection={selectedBanescoSuggestions}
                                            onSelectionChange={(event) => setSelectedBanescoSuggestions(
                                                (event.value || []).filter((row) => row.confidence !== 'low')
                                            )}
                                            dataKey="selectionKey"
                                            paginator
                                            rows={20}
                                            emptyMessage="No hay terminales sin mapear con datos comparables."
                                        >
                                            <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
                                            <Column header="Terminal" body={(row) => [row.terminalName, row.terminalId].filter(Boolean).join(' · ')} />
                                            <Column header="Máquina sugerida" body={(row) => [row.parlevelSystemId, row.routeName, row.machineName, row.locationName].filter(Boolean).join(' · ')} />
                                            <Column header="Banesco" body={(row) => money(row.terminalTotal)} />
                                            <Column header="Parlevel" body={(row) => money(row.machineTotal)} />
                                            <Column header="Diferencia" body={(row) => money(row.monthlyDifference)} />
                                            <Column header="Correlación" body={(row) => percent(row.correlation)} />
                                            <Column header="Error diario" body={(row) => percent(row.normalizedError)} />
                                            <Column
                                                header="Confianza"
                                                body={(row) => <Tag
                                                    value={{ high: 'Alta', medium: 'Media', low: 'Baja' }[row.confidence] || row.confidence}
                                                    severity={{ high: 'success', medium: 'warning', low: 'danger' }[row.confidence] || 'info'}
                                                />}
                                            />
                                        </DataTable>
                                    </>
                                ) : null}
                            </Card>
                            <Card title="Transacciones oficiales de ePay / Yappy" className="revenue-reconciliation__source-config">
                                <p>La sincronización consulta directamente el reporte mensual oficial. Los estados USADO e INGRESADO se conservan y presentan por separado; no se suman entre sí.</p>
                                <div className="revenue-reconciliation__row-actions">
                                    <Button
                                        label={`Sincronizar ePay / Yappy ${month}`}
                                        icon="pi pi-refresh"
                                        loading={busy || yappySource.automation?.running}
                                        disabled={!yappySource.automation?.configured}
                                        onClick={pullYappyPeriod}
                                    />
                                    <Tag
                                        value={yappySource.automation?.configured ? 'Conexión configurada' : 'Configuración pendiente'}
                                        severity={yappySource.automation?.configured ? 'success' : 'warning'}
                                    />
                                    {yappySource.automation?.running ? <Tag value="Sincronizando reporte mensual" severity="info" /> : null}
                                    {!yappySource.automation?.running && yappySource.automation?.lastRun?.status === 'completed' ? (
                                        <Tag value={`Completada · ${yappySource.automation.lastRun.records || 0} registros`} severity="success" />
                                    ) : null}
                                    {!yappySource.automation?.running && yappySource.automation?.lastRun?.status === 'failed' ? (
                                        <Tag value={`Última sincronización falló: ${yappySource.automation.lastRun.message}`} severity="danger" />
                                    ) : null}
                                </div>
                                <div className="revenue-reconciliation__banesco-preview">
                                    <div><span>Registros</span><b>{yappySource.totals?.records || 0}</b></div>
                                    <div><span>Máquinas ePay</span><b>{yappySource.totals?.machines || 0}</b></div>
                                    <div><span>USADO</span><b>{money(yappySource.totals?.used?.amount)}</b></div>
                                    <div><span>Registros USADO</span><b>{yappySource.totals?.used?.records || 0}</b></div>
                                    <div><span>INGRESADO</span><b>{money(yappySource.totals?.entered?.amount)}</b></div>
                                    <div><span>Registros INGRESADO</span><b>{yappySource.totals?.entered?.records || 0}</b></div>
                                    <div><span>Otros estados</span><b>{money(yappySource.totals?.other?.amount)}</b></div>
                                    <div><span>Registros otros</span><b>{yappySource.totals?.other?.records || 0}</b></div>
                                </div>
                                <h4>Lotes sincronizados para {month}</h4>
                                <DataTable value={yappySource.batches || []} paginator rows={5} emptyMessage="No hay lotes de ePay / Yappy para este período.">
                                    <Column header="Cobertura" body={(row) => `${row.metadata?.coverageStart || '—'} – ${row.metadata?.coverageEnd || '—'}`} />
                                    <Column field="recordCount" header="Registros" />
                                    <Column header="Estado" body={(row) => <Tag value={row.status} severity={row.status === 'validated' ? 'success' : 'danger'} />} />
                                    <Column header="Recibido" body={(row) => dateTime(row.receivedAt)} />
                                    <Column header="Checksum" body={(row) => <code>{String(row.checksum || '').slice(0, 12)}</code>} />
                                </DataTable>
                                <h4>Máquinas reportadas ({(yappySource.machines || []).length})</h4>
                                <DataTable value={yappySource.machines || []} paginator rows={10} emptyMessage="Sin transacciones para el período.">
                                    <Column field="machineIdentifier" header="ID ePay" />
                                    <Column field="machineCode" header="Código" />
                                    <Column header="Máquina" body={(row) => row.mapping?.canonicalMachineName || row.machineLabel} />
                                    <Column header="Ruta Parlevel" body={(row) => row.mapping?.canonicalRouteName || '—'} />
                                    <Column field="records" header="Registros" />
                                    <Column header="USADO" body={(row) => money(row.usedAmount)} />
                                    <Column header="INGRESADO" body={(row) => money(row.enteredAmount)} />
                                    <Column header="Otros" body={(row) => money(row.otherAmount)} />
                                    <Column header="Mapeo" body={(row) => <Tag value={row.mapping ? 'Mapeada' : 'Sin mapear'} severity={row.mapping ? 'success' : 'warning'} />} />
                                </DataTable>
                                <small>La vinculación de estas máquinas con Parlevel seguirá bloqueando el cierre hasta ser revisada.</small>
                            </Card>
                            <Card title="Cuentas de ingresos de Coffee & Vending" className="revenue-reconciliation__source-config">
                                <p>Seleccione únicamente cuentas confirmadas desde el catálogo oficial sincronizado de QuickBooks.</p>
                                <label>Cuentas de ingresos</label>
                                <MultiSelect
                                    value={accountIds}
                                    options={(sourceConfig?.availableQuickBooksAccounts || []).map((account) => ({
                                        value: account.id,
                                        label: [account.number, account.name, account.accountType].filter(Boolean).join(' · ')
                                    }))}
                                    onChange={(event) => setAccountIds(event.value)}
                                    optionLabel="label"
                                    optionValue="value"
                                    filter
                                    display="chip"
                                    placeholder="Seleccione cuentas oficiales"
                                />
                                <small>Unidad: {sourceConfig?.quickBooksBusinessUnit || 'coffee_vending'}</small>
                                <div className="revenue-reconciliation__actions">
                                    <Button label="Actualizar catálogo oficial" icon="pi pi-refresh" className="p-button-outlined" loading={busy} onClick={refreshQuickBooksAccounts} />
                                    <Button label="Guardar configuración" icon="pi pi-save" onClick={saveSourceConfig} />
                                </div>
                            </Card>
                            <Card title="Mapeo de rutas entre fuentes oficiales" className="revenue-reconciliation__source-config">
                                <div className="revenue-reconciliation__mapping-form">
                                    <Dropdown
                                        value={mapping.source}
                                        options={[
                                            { label: 'QuickBooks', value: 'quickbooks' },
                                            { label: 'Parlevel', value: 'parlevel' },
                                            { label: 'ePay / Yappy', value: 'epay_yappy' }
                                        ]}
                                        onChange={(event) => setMapping({ ...mapping, source: event.value })}
                                    />
                                    <InputText placeholder="ID oficial de origen" value={mapping.sourceId} onChange={(event) => setMapping({ ...mapping, sourceId: event.target.value })} />
                                    <InputText placeholder="Nombre en el origen" value={mapping.sourceLabel} onChange={(event) => setMapping({ ...mapping, sourceLabel: event.target.value })} />
                                    <InputText placeholder="ID canónico de ruta" value={mapping.canonicalRouteId} onChange={(event) => setMapping({ ...mapping, canonicalRouteId: event.target.value })} />
                                    <InputText placeholder="Nombre canónico de ruta" value={mapping.canonicalRouteName} onChange={(event) => setMapping({ ...mapping, canonicalRouteName: event.target.value })} />
                                    <input type="date" value={mapping.effectiveFrom} onChange={(event) => setMapping({ ...mapping, effectiveFrom: event.target.value })} />
                                    <Button label="Crear mapeo" icon="pi pi-link" onClick={saveMapping} />
                                </div>
                                <DataTable value={mappings} paginator rows={10} emptyMessage="No hay mapeos configurados.">
                                    <Column field="source" header="Fuente" />
                                    <Column field="sourceId" header="ID oficial" />
                                    <Column field="sourceLabel" header="Nombre de origen" />
                                    <Column field="canonicalRouteName" header="Ruta canónica" />
                                    <Column field="effectiveFrom" header="Vigente desde" />
                                </DataTable>
                            </Card>
                        </TabPanel>
                        <TabPanel header={`Ajustes y cierre (${detail.adjustments?.length || 0})`}>
                            <div className="revenue-reconciliation__actions">
                                <Button label="Nuevo ajuste" icon="pi pi-plus" onClick={() => setAdjustmentOpen(true)} disabled={period.status === 'closed'} />
                                {['draft', 'reopened'].includes(period.status) ? <Button label="Enviar a revisión" icon="pi pi-send" className="p-button-warning" onClick={() => periodAction('submit')} /> : null}
                                {period.status === 'in_review' ? <Button label="Cerrar período" icon="pi pi-lock" className="p-button-success" disabled={period.blockingReasons?.length > 0} onClick={() => {
                                    const note = window.prompt('Nota obligatoria de aprobación y cierre:');
                                    if (note?.trim()) periodAction('close', { note: note.trim() });
                                }} /> : null}
                                {period.status === 'closed' ? <Button label="Reabrir" icon="pi pi-lock-open" className="p-button-danger p-button-outlined" onClick={() => {
                                    const reason = window.prompt('Motivo obligatorio de reapertura:');
                                    if (reason) periodAction('reopen', { reason });
                                }} /> : null}
                            </div>
                            <DataTable value={detail.adjustments || []} emptyMessage="No hay ajustes en este período.">
                                <Column field="category" header="Categoría" />
                                <Column header="Importe" body={(row) => money(row.amount)} />
                                <Column field="reason" header="Motivo" />
                                <Column field="evidenceReference" header="Evidencia / documento" />
                                <Column header="Creado por" body={(row) => row.createdBy?.email || '—'} />
                                <Column header="Estado" body={(row) => <Tag value={statusLabels[row.status] || row.status} severity={severity(row.status)} />} />
                                <Column header="Acción" body={adjustmentActions} />
                            </DataTable>
                        </TabPanel>
                        <TabPanel header="Historial y exportación">
                            <div className="revenue-reconciliation__actions">
                                <Button label="Exportar CSV de partidas" icon="pi pi-download" className="p-button-outlined" onClick={() => exportData('csv')} />
                                <Button label="Exportar XLSX de partidas" icon="pi pi-file-excel" className="p-button-success" onClick={() => exportData('xlsx')} />
                                <Button label="Exportar JSON auditado" icon="pi pi-shield" className="p-button-outlined" onClick={exportAuditJson} />
                            </div>
                            <DataTable value={detail.history || []} emptyMessage="No hay eventos de auditoría.">
                                <Column field="action" header="Evento" />
                                <Column header="Fecha" body={(row) => dateTime(row.occurredAt)} />
                                <Column header="Usuario" body={(row) => row.actor?.email || '—'} />
                                <Column header="Detalle" body={(row) => <code>{JSON.stringify(row.details || {})}</code>} />
                            </DataTable>
                        </TabPanel>
                    </TabView>
                </>
            )}

            <Dialog
                header="Confirmar equivalencia Banesco – Parlevel"
                visible={Boolean(banescoMappingDraft)}
                onHide={() => setBanescoMappingDraft(null)}
                style={{ width: 'min(680px, 95vw)' }}
            >
                {banescoMappingDraft ? (
                    <div className="revenue-reconciliation__form">
                        <p>
                            Terminal <b>{banescoMappingDraft.row.terminalId}</b> → máquina{' '}
                            <b>{banescoMappingDraft.parlevelSystemId}</b>
                        </p>
                        <label>Estado de verificación *</label>
                        <Dropdown
                            value={banescoMappingDraft.verificationStatus}
                            options={[
                                { label: '* Provisional — pendiente de evidencia definitiva', value: 'provisional' },
                                { label: 'Verificada — evidencia física u oficial disponible', value: 'verified' }
                            ]}
                            onChange={(event) => setBanescoMappingDraft({
                                ...banescoMappingDraft,
                                verificationStatus: event.value
                            })}
                        />
                        <label>
                            {banescoMappingDraft.verificationStatus === 'verified'
                                ? 'Evidencia física u oficial *'
                                : 'Razón de provisionalidad *'}
                        </label>
                        <InputText
                            value={banescoMappingDraft.verificationNote}
                            maxLength={500}
                            onChange={(event) => setBanescoMappingDraft({
                                ...banescoMappingDraft,
                                verificationNote: event.target.value
                            })}
                        />
                        <small>
                            La correlación de ventas por sí sola no confirma una equivalencia. El cambio y su estado quedarán auditados.
                        </small>
                        <div className="revenue-reconciliation__actions">
                            <Button label="Cancelar" className="p-button-outlined" onClick={() => setBanescoMappingDraft(null)} />
                            <Button label="Guardar equivalencia" icon="pi pi-save" onClick={saveBanescoMapping} />
                        </div>
                    </div>
                ) : null}
            </Dialog>

            <Dialog header="Nuevo ajuste controlado" visible={adjustmentOpen} onHide={() => setAdjustmentOpen(false)} style={{ width: 'min(680px, 95vw)' }}>
                <div className="revenue-reconciliation__form">
                    <label>Categoría *</label>
                    <InputText value={adjustment.category} onChange={(event) => setAdjustment({ ...adjustment, category: event.target.value })} />
                    <label>Importe *</label>
                    <InputNumber value={adjustment.amount} mode="currency" currency="USD" locale="en-US" onValueChange={(event) => setAdjustment({ ...adjustment, amount: event.value })} />
                    <label>Motivo *</label>
                    <InputText value={adjustment.reason} onChange={(event) => setAdjustment({ ...adjustment, reason: event.target.value })} />
                    <label>Evidencia o identificador documental *</label>
                    <InputText value={adjustment.evidenceReference} onChange={(event) => setAdjustment({ ...adjustment, evidenceReference: event.target.value })} />
                    <label>Ruta</label>
                    <InputText value={adjustment.routeName} onChange={(event) => setAdjustment({ ...adjustment, routeName: event.target.value })} />
                    <label>Máquina</label>
                    <InputText value={adjustment.machineName} onChange={(event) => setAdjustment({ ...adjustment, machineName: event.target.value })} />
                    <small>Otro Admin o Super Admin deberá aprobar este ajuste.</small>
                    <Button label="Guardar ajuste" icon="pi pi-save" onClick={saveAdjustment} />
                </div>
            </Dialog>
        </div>
    );
};

export default RevenueReconciliation;
